export type ChecklistPriority = "low" | "medium" | "high" | "critical";

export type ChecklistItem = {
  readonly id: string;
  readonly label: string;
  readonly description?: string;
  readonly weight?: number;
  readonly priority?: ChecklistPriority;
  readonly nextStepId?: string;
};

export type ChecklistTier = {
  readonly id: string;
  readonly label: string;
  readonly summary: string;
  readonly minPercent: number;
  readonly maxPercent: number;
};

export type ChecklistSchema = {
  readonly id: string;
  readonly items: readonly ChecklistItem[];
  readonly tiers: readonly ChecklistTier[];
};

export type ChecklistResult = {
  readonly schemaId: string;
  readonly completedIds: readonly string[];
  readonly completedCount: number;
  readonly totalCount: number;
  readonly score: number;
  readonly maxScore: number;
  readonly percent: number;
  readonly tier: ChecklistTier;
  readonly incompleteItems: readonly ChecklistItem[];
  readonly nextStepIds: readonly string[];
  readonly complete: boolean;
};

const priorityRank: Record<ChecklistPriority, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
};

const unique = <T>(values: readonly T[]): T[] => [...new Set(values)];

function tierFor(schema: ChecklistSchema, percent: number): ChecklistTier {
  const sortedTiers = [...schema.tiers].sort((a, b) => a.minPercent - b.minPercent);
  const tier = sortedTiers.find((candidate) => percent >= candidate.minPercent && percent <= candidate.maxPercent);

  return (
    tier ??
    sortedTiers.at(-1) ?? {
      id: "unclassified",
      label: "",
      summary: "",
      minPercent: 0,
      maxPercent: 100,
    }
  );
}

export function evaluateChecklist(schema: ChecklistSchema, completedIds: readonly string[]): ChecklistResult {
  const completed = new Set(completedIds);
  const maxScore = schema.items.reduce((sum, item) => sum + (item.weight ?? 1), 0);
  const score = schema.items.reduce((sum, item) => sum + (completed.has(item.id) ? item.weight ?? 1 : 0), 0);
  const percent = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
  const incompleteItems = schema.items
    .filter((item) => !completed.has(item.id))
    .sort((a, b) => (priorityRank[b.priority ?? "low"] ?? 0) - (priorityRank[a.priority ?? "low"] ?? 0));

  return {
    schemaId: schema.id,
    completedIds: unique(completedIds.filter((id) => schema.items.some((item) => item.id === id))),
    completedCount: schema.items.filter((item) => completed.has(item.id)).length,
    totalCount: schema.items.length,
    score,
    maxScore,
    percent,
    tier: tierFor(schema, percent),
    incompleteItems,
    nextStepIds: unique(incompleteItems.map((item) => item.nextStepId).filter((id): id is string => Boolean(id))),
    complete: incompleteItems.length === 0,
  };
}
